import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Flex, VStack, IconButton, Tooltip, Drawer, DrawerOverlay, DrawerContent,
  DrawerBody, DrawerCloseButton, DrawerHeader, useBreakpointValue, Text,
} from '@chakra-ui/react';
import {
  FiHome, FiInbox, FiBarChart2, FiCpu, FiSettings, FiUser, FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: FiHome, exact: true },
  { to: '/triage', label: 'Triage queue', icon: FiInbox, badge: 'triage' },
  {
    to: '/metrics',
    label: 'Metrics',
    icon: FiBarChart2,
    sectionPrefixes: ['/metrics', '/test-runs', '/test-cases', '/test-groups', '/test-case-groups', '/compare'],
    children: [
      { to: '/test-runs', label: 'Test runs' },
      { to: '/compare', label: 'Compare runs' },
    ],
  },
  {
    to: '/analysis/release-readiness',
    label: 'AI Analysis',
    icon: FiCpu,
    matchPrefix: '/analysis',
    children: [
      { to: '/analysis/release-readiness', label: 'Release readiness' },
      { to: '/analysis/failure-analysis', label: 'Failure Analysis' },
      { to: '/analysis/testing-gaps', label: 'Testing gaps' },
    ],
  },
];

const SETTINGS_ITEM = { to: '/settings', icon: FiSettings, label: 'Settings' };

function useTriageBadge() {
  const { effectiveProjectParam } = useProjectContext();
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const q = { bucket: 'new' };
    if (effectiveProjectParam) q.project = effectiveProjectParam;
    client
      .get('/api/tests', { query: q })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setCount(d.counts ? d.counts.new || 0 : d.total || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [effectiveProjectParam]);
  return count;
}

/**
 * Subscribes to GET /api/test-runs/live-stream (SSE) instead of polling, so
 * this card - visible on every page - reflects a run's progress within
 * milliseconds instead of on the next poll tick. Tracks every currently-
 * running run (there could be more than one) and always displays the most
 * recently started.
 */
function useLiveRun() {
  const { effectiveProjectParam } = useProjectContext();
  const [run, setRun] = useState(null);

  useEffect(() => {
    const runs = new Map();
    const pickDisplayRun = () => {
      const list = [...runs.values()].sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
      setRun(list[0] || null);
    };

    const url = new URL(`${API_BASE_URL}/api/test-runs/live-stream`, window.location.origin);
    if (effectiveProjectParam) url.searchParams.set('project', effectiveProjectParam);
    const es = new EventSource(url.toString(), { withCredentials: true });

    es.addEventListener('snapshot', (e) => {
      const { runs: initial } = JSON.parse(e.data);
      runs.clear();
      (initial || []).forEach((r) => runs.set(r.id, r));
      pickDisplayRun();
    });
    es.addEventListener('started', (e) => {
      const { runId, run: r } = JSON.parse(e.data);
      runs.set(runId, r);
      pickDisplayRun();
    });
    es.addEventListener('event', (e) => {
      const { runId, run: r } = JSON.parse(e.data);
      if (runs.has(runId)) { runs.set(runId, r); pickDisplayRun(); }
    });
    es.addEventListener('finished', (e) => {
      const { runId } = JSON.parse(e.data);
      runs.delete(runId);
      pickDisplayRun();
    });

    return () => es.close();
  }, [effectiveProjectParam]);

  return run;
}

function LiveRunCard({ collapsed, onClose }) {
  const run = useLiveRun();
  if (!run || collapsed) return null;
  const ran = (run.passedTests || 0) + (run.failedTests || 0) + (run.skippedTests || 0);
  const pct = run.totalTests ? Math.min(100, Math.round((ran / run.totalTests) * 100)) : 0;
  return (
    <Box
      as={Link}
      to={`/test-runs/${run.id}/live`}
      onClick={onClose}
      display="block"
      m={3}
      p="13px 14px"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      bg="gray.50"
      _hover={{ borderColor: 'gray.300' }}
    >
      <Flex align="center" gap={2}>
        <Box boxSize="8px" borderRadius="full" bg="brand.500" sx={{ animation: 'pulse 1.4s infinite' }} />
        <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="gray.400">
          Running now
        </Text>
      </Flex>
      <Text fontSize="12px" fontFamily="mono" color="gray.700" mt={2} noOfLines={1}>
        {run.name} #{String(run.id).slice(0, 6)}
      </Text>
      <Box mt={2} h="5px" borderRadius="full" bg="gray.150" overflow="hidden">
        <Box h="100%" w={`${pct}%`} bg="brand.500" />
      </Box>
      <Flex justify="space-between" mt="6px" fontSize="11px" fontFamily="mono" color="gray.400">
        <span>{run.passedTests || 0}p · {run.failedTests || 0}f</span>
        <span>{pct}%</span>
      </Flex>
    </Box>
  );
}

export default function Sidebar({ isMobileMenuOpen, onCloseMobileMenu, collapsed, setCollapsed }) {
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const btnRef = useRef();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const effCollapsed = isMobile ? false : collapsed !== undefined ? collapsed : internalCollapsed;
  const setEff = setCollapsed || setInternalCollapsed;
  const triageCount = useTriageBadge();

  const isItemActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname === item.to;
  const inSection = (item) =>
    !isItemActive(item) &&
    (((item.children || []).some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'))) ||
      (item.matchPrefix && location.pathname.startsWith(item.matchPrefix)) ||
      (item.sectionPrefixes || []).some((p) => location.pathname === p || location.pathname.startsWith(p + '/')));

  const navRow = (item) => {
    const active = isItemActive(item);
    const section = inSection(item);
    const accent = active || section;
    return (
      <Box key={item.label} mb={effCollapsed ? 1 : 0.5}>
        <Tooltip label={item.label} placement="right" isDisabled={!effCollapsed} openDelay={300}>
          <Flex
            as={Link}
            to={item.to}
            align="center"
            gap={effCollapsed ? 0 : 2.5}
            w="100%"
            px="10px"
            py="9px"
            borderRadius="9px"
            bg={active ? 'brand.50' : 'transparent'}
            _hover={{ bg: 'gray.100' }}
            onClick={isMobile ? onCloseMobileMenu : undefined}
            style={{ textDecoration: 'none' }}
          >
            <Box as={item.icon} boxSize="17px" color={accent ? 'brand.600' : 'gray.400'} flexShrink={0} strokeWidth={1.7} />
            {!effCollapsed && (
              <>
                <Text fontSize="14px" fontWeight={accent ? '600' : '500'} color={accent ? 'brand.700' : 'gray.700'}>
                  {item.label}
                </Text>
                {item.badge === 'triage' && triageCount ? (
                  <Box
                    ml="auto"
                    bg="status.failBg"
                    color="status.failText"
                    borderRadius="full"
                    px="7px"
                    fontSize="11px"
                    fontWeight="700"
                    fontFamily="mono"
                  >
                    {triageCount}
                  </Box>
                ) : null}
              </>
            )}
          </Flex>
        </Tooltip>

        {!effCollapsed && item.children && (
          <VStack align="stretch" spacing="1px" mt="1px" ml="22px" pl="12px" borderLeft="1px solid" borderColor="gray.200">
            {item.children.map((child) => {
              const ca = location.pathname === child.to || location.pathname.startsWith(child.to + '/');
              return (
                <Box
                  key={child.to}
                  as={Link}
                  to={child.to}
                  px="10px"
                  py="7px"
                  borderRadius="8px"
                  fontSize="13px"
                  bg={ca ? 'brand.50' : 'transparent'}
                  color={ca ? 'brand.600' : 'gray.500'}
                  fontWeight={ca ? '600' : '500'}
                  _hover={{ color: 'gray.900', bg: 'gray.100' }}
                  onClick={isMobile ? onCloseMobileMenu : undefined}
                  style={{ textDecoration: 'none' }}
                >
                  {child.label}
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    );
  };

  const content = (
    <Flex
      as="nav"
      direction="column"
      h="100vh"
      w={effCollapsed ? '72px' : { base: '100%', md: '220px' }}
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      pt={4}
      px={effCollapsed ? 2 : 3}
      position="fixed"
      top="64px"
      left={0}
      zIndex={100}
      transition="all 0.2s"
    >
      {!isMobile && (
        <Box position="absolute" top="20px" right="-12px" zIndex={10}>
          <IconButton
            icon={effCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            aria-label={effCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setEff(!effCollapsed)}
            size="xs"
            variant="solid"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="full"
            color="gray.500"
            boxShadow="chip"
            _hover={{ color: 'brand.600' }}
          />
        </Box>
      )}

      <Flex direction="column" flex={1} minH={0} overflowY="auto" px={2} py={2} css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
        {NAV_ITEMS.map(navRow)}
      </Flex>

      <LiveRunCard collapsed={effCollapsed} onClose={isMobile ? onCloseMobileMenu : undefined} />

      <Box pb={4} px={2}>
        <VStack align="stretch" spacing={0.5}>
          {[SETTINGS_ITEM, { to: '/profile', icon: FiUser, label: 'Profile' }].map((item) => {
            const active = location.pathname.startsWith(item.to);
            return (
              <Flex
                key={item.to}
                as={Link}
                to={item.to}
                align="center"
                gap={effCollapsed ? 0 : 2.5}
                px="10px"
                py="9px"
                borderRadius="9px"
                bg={active ? 'brand.50' : 'transparent'}
                _hover={{ bg: 'gray.100' }}
                onClick={isMobile ? onCloseMobileMenu : undefined}
                style={{ textDecoration: 'none' }}
              >
                <Box as={item.icon} boxSize="17px" color={active ? 'brand.600' : 'gray.400'} flexShrink={0} strokeWidth={1.7} />
                {!effCollapsed && (
                  <Text fontSize="14px" fontWeight={active ? '600' : '500'} color={active ? 'brand.700' : 'gray.700'}>
                    {item.label}
                  </Text>
                )}
              </Flex>
            );
          })}
        </VStack>
      </Box>
    </Flex>
  );

  if (isMobile) {
    return (
      <Drawer isOpen={isMobileMenuOpen} placement="left" onClose={onCloseMobileMenu} finalFocusRef={btnRef} size="xs">
        <DrawerOverlay />
        <DrawerContent bg="white">
          <DrawerCloseButton mt={2} />
          <DrawerHeader borderBottomWidth={0} fontWeight="700" fontSize="lg">Menu</DrawerHeader>
          <DrawerBody p={0}>{content}</DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return <Box as="aside">{content}</Box>;
}
